import { useState, useMemo, useCallback } from 'react';
import { message } from 'antd';
import { inspectionService } from '@/services/inspection.service';
import { InspectionRequest } from '@/services/types/inspection.types';
import { AmostrasData } from '../types/laudo-wizard.types';

interface UsePreliminaryCalculationsReturn {
  hasDraft: boolean;
  draftData: AmostrasData | null;
  saveDraft: (data: AmostrasData) => Promise<void>;
  clearDraft: () => Promise<void>;
  saving: boolean;
}

export function usePreliminaryCalculations(inspection: InspectionRequest): UsePreliminaryCalculationsReturn {
  const [saving, setSaving] = useState(false);

  const itemId = inspection.items?.[0]?.id;
  const rawData = inspection.items?.[0]?.preliminaryCalculationsData;

  const draftData = useMemo<AmostrasData | null>(() => {
    if (!rawData) return null;
    try {
      return JSON.parse(rawData) as AmostrasData;
    } catch {
      return null;
    }
  }, [rawData]);

  const hasDraft = draftData !== null;

  const saveDraft = useCallback(async (data: AmostrasData) => {
    if (!itemId) return;
    setSaving(true);
    try {
      await inspectionService.updatePreliminaryCalculations(itemId, JSON.stringify(data));
      // Atualizar o objeto de inspeção localmente para refletir a mudança
      if (inspection.items?.[0]) {
        inspection.items[0].preliminaryCalculationsData = JSON.stringify(data);
      }
      message.success('Memorial de cálculo preliminar salvo com sucesso');
    } catch (error) {
      message.error('Erro ao salvar memorial de cálculo preliminar');
      throw error;
    } finally {
      setSaving(false);
    }
  }, [itemId, inspection]);

  const clearDraft = useCallback(async () => {
    if (!itemId) return;
    setSaving(true);
    try {
      await inspectionService.updatePreliminaryCalculations(itemId, null);
      if (inspection.items?.[0]) {
        inspection.items[0].preliminaryCalculationsData = undefined;
      }
    } catch (error) {
      console.error('Erro ao limpar memorial preliminar:', error);
    } finally {
      setSaving(false);
    }
  }, [itemId, inspection]);

  return { hasDraft, draftData, saveDraft, clearDraft, saving };
}
